import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/apiClient';
import { type InvoiceNotificationDispatch, Invoice } from '../types';

type ApiInvoicesMeta = {
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
};

export type InvoicesResponse = {
  data: Invoice[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
};

type ApiInvoicePayload = {
  invoiceId?: number;
  userId?: number;
  customerName?: string;
  customerEmail?: string;
  serviceDate?: string;
  isOpen?: boolean;
  firstOpenedAt?: string;
  first_opened_at?: string;
  lastOpenedAt?: string;
  last_opened_at?: string;
  clickCount?: number;
  click_count?: number;
  amount?: string;
  description?: string;
  status?: string;
  createdAt?: string;
  transactionId?: string;
  transactionStatus?: string;
  notificationDispatch?: string;
  notification_dispatch?: string;
};

const normalizeNotificationDispatch = (raw?: string): InvoiceNotificationDispatch | undefined => {
  if (!raw || typeof raw !== 'string') {
    return undefined;
  }
  const normalized = raw.trim().toUpperCase().replace(/-/g, '_');
  if (normalized === 'EMAIL_ONLY' || normalized === 'SMS_ONLY' || normalized === 'BOTH') {
    return normalized;
  }
  return undefined;
};

export type BulkEmailStats = {
  failed: number;
  sent: number;
  skipped: number;
  totalDrafts: number;
};

const extractInvoicesFromResponse = (payload: unknown): ApiInvoicePayload[] => {
  if (Array.isArray(payload)) return payload as ApiInvoicePayload[];

  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: ApiInvoicePayload[] }).data;
  }

  if (payload && typeof payload === 'object' && Array.isArray((payload as { invoices?: unknown }).invoices)) {
    return (payload as { invoices: ApiInvoicePayload[] }).invoices;
  }

  return [];
};

const mapApiInvoice = (item: ApiInvoicePayload, index: number): Invoice => {
  const notificationDispatch = normalizeNotificationDispatch(
    item.notificationDispatch ?? item.notification_dispatch
  );

  return {
    id: item.invoiceId ?? index + 1,
    invoiceId: item.invoiceId ?? index + 1,
    userId: item.userId ?? 0,
    customerName: item.customerName ?? 'Unknown',
    customerEmail: item.customerEmail ?? 'unknown@example.com',
    serviceDate: item.serviceDate ?? '--',
    isOpen: item.isOpen ?? false,
    firstOpenedAt: item.firstOpenedAt ?? item.first_opened_at ?? '',
    lastOpenedAt: item.lastOpenedAt ?? item.last_opened_at ?? '',
    clickCount: item.clickCount ?? item.click_count ?? 0,
    amount: item.amount ?? '0.00',
    description: item.description ?? 'No description provided.',
    status: item.status ?? 'UNKNOWN',
    createdAt: item.createdAt ?? '',
    transactionId: item.transactionId ?? 'N/A',
    transactionStatus: item.transactionStatus ?? 'UNKNOWN',
    ...(notificationDispatch !== undefined ? { notificationDispatch } : {}),
  };
};

const extractMetaFromResponse = (payload: unknown, fallbackCount: number): InvoicesResponse['meta'] => {
  if (payload && typeof payload === 'object' && (payload as { meta?: unknown }).meta) {
    const rawMeta = (payload as { meta?: ApiInvoicesMeta }).meta ?? {};
    return {
      total: rawMeta.total ?? fallbackCount,
      page: rawMeta.page ?? 1,
      limit: rawMeta.limit ?? fallbackCount,
      totalPages: rawMeta.totalPages ?? 1,
    };
  }

  return {
    total: fallbackCount,
    page: 1,
    limit: fallbackCount,
    totalPages: 1,
  };
};

const fetchInvoices = async (
  page: number,
  limit: number,
  statusFilter: string,
  searchTerm: string,
  sortField: string,
  sortDirection: 'asc' | 'desc'
): Promise<InvoicesResponse> => {
  const normalizedStatus = statusFilter.trim().toUpperCase();
  const normalizedSearchTerm = searchTerm.trim();
  const statusParam =
    normalizedStatus && normalizedStatus !== 'ALL'
      ? `&status=${encodeURIComponent(normalizedStatus.toLowerCase())}`
      : '';
  const searchParam = normalizedSearchTerm.length > 0 ? `&search=${encodeURIComponent(normalizedSearchTerm)}` : '';
  const sortFieldParam = sortField.trim().length > 0 ? `&sortBy=${encodeURIComponent(sortField)}` : '';
  const sortDirectionParam = sortField.trim().length > 0 ? `&sortDirection=${encodeURIComponent(sortDirection)}` : '';
  const responseData = await apiClient.getJson<unknown>(
    `/invoices?page=${page}&limit=${limit}${statusParam}${searchParam}${sortFieldParam}${sortDirectionParam}`
  );
  const mappedInvoices = extractInvoicesFromResponse(responseData).map(mapApiInvoice);
  const meta = extractMetaFromResponse(responseData, mappedInvoices.length);

  return {
    data: mappedInvoices,
    meta,
  };
};

const sendInvoiceEmail = async (invoiceId: number): Promise<unknown> => {
  return apiClient.postJson<unknown>(`/invoices/${invoiceId}/send-email`, {});
};

const sendBulkEmails = async (): Promise<BulkEmailStats> => {
  const responseData = await apiClient.postJson<unknown>('/invoices/bulk/send-emails', {}, { timeoutMs: 120000 });
  const payload = (responseData ?? {}) as Partial<BulkEmailStats>;

  return {
    failed: Number(payload.failed ?? 0),
    sent: Number(payload.sent ?? 0),
    skipped: Number(payload.skipped ?? 0),
    totalDrafts: Number(payload.totalDrafts ?? 0),
  };
};

type UpdateInvoiceInput = {
  invoiceId: number;
  amount: string;
  description: string;
};

const updateInvoice = async ({ invoiceId, amount, description }: UpdateInvoiceInput): Promise<unknown> => {
  return apiClient.patchJson<unknown>(`/invoices/${invoiceId}`, {
    amount,
    description,
  });
};

export const useInvoicesQuery = (
  enabled: boolean,
  page: number,
  limit: number,
  statusFilter: string,
  searchTerm = '',
  sortField = '',
  sortDirection: 'asc' | 'desc' = 'asc'
) =>
  useQuery({
    queryKey: ['invoices', page, limit, statusFilter, searchTerm, sortField, sortDirection],
    queryFn: () => fetchInvoices(page, limit, statusFilter, searchTerm, sortField, sortDirection),
    enabled,
  });

export const useSendInvoiceEmailMutation = () =>
  useMutation({
    mutationFn: sendInvoiceEmail,
  });

export const useSendBulkEmailsMutation = () =>
  useMutation({
    mutationFn: sendBulkEmails,
  });

export const useUpdateInvoiceMutation = () =>
  useMutation({
    mutationFn: updateInvoice,
  });
